/* eslint-disable sonarjs/cognitive-complexity */
import * as React from 'react';
import { Alert, Button, Card, Form, Table } from 'react-bootstrap';
import { Pencil } from 'react-bootstrap-icons';
import { useLoaderData, useLocation } from 'react-router-dom';

import { FormButtons } from '@/components/formbuttons';
import { InfoTable } from '@/components/infotable';
import { InputNumber } from '@/components/input-number';
import { InputTimeDuration } from '@/components/input-timeduration';
import { ListLink } from '@/components/listlink';
import { ModalForm } from '@/components/modal-form';
import { Switch } from '@/components/switch';
import { deleteSingle, getSingle, postSingle, putSingle } from '@/helper/apicalls';
import { Report } from '@/helper/interfaces';
import { formatDate, formatDateShort, formatMoney, formatTime, removeKeysWithPrefix } from '@/helper/utility';

import { baseURL } from './const';

export async function singleLoader({ params }: any) {
  if (Number.isInteger(Number.parseInt(params.id, 10))) {
    return getSingle(baseURL, params.id);
  } else {
    throw new TypeError('404! This is not a valid URL.');
  }
}

export async function singleAction({ request, params }: any) {
  const body = Object.fromEntries(await request.formData());
  const redirectTo = body.redirectTo;
  delete body.redirectTo;

  if (body.mixerIds) body.mixerIds = JSON.parse(body.mixerIds);
  const bodyCleaned = removeKeysWithPrefix(body, 'element');

  if (body.workOrderServiceId) body.workOrderServiceId = JSON.parse(body.workOrderServiceId);

  body.customOT = body.customOT == 'on' ? true : false;

  if (request.method == 'POST') {
    return postSingle(baseURL, bodyCleaned);
  } else if (request.method == 'PUT') {
    return putSingle(baseURL, params.id, body);
  } else if (request.method == 'DELETE') {
    return deleteSingle(baseURL, params.id, redirectTo);
  } else {
    // eslint-disable-next-line unicorn/no-null
    return null;
  }
}

export function Single() {
  const data = useLoaderData() as Report;

  const location = useLocation();
  const [redirectTo] = React.useState(location.state?.from || baseURL);

  const isNotFinished = Object.values(data.workOrders.map((workOrder) => workOrder.isFinished)).includes(false);

  const modalRate = React.useRef(null);

  const [workOrderServiceId, setWorkOrderServiceId] = React.useState<number>();
  const [overridePayRate, setOverridePayRate] = React.useState<number>();
  const [customOT, setCustomOT] = React.useState<boolean>();
  const [hoursOT, setHoursOT] = React.useState<number>();
  const [hoursDT, setHoursDT] = React.useState<number>();
  function editRate(id: number, rate: number, hoursOT: number, hoursDT: number) {
    setWorkOrderServiceId(id);
    setOverridePayRate(rate);
    setCustomOT(hoursOT >= 0 || hoursDT >= 0);
    setHoursOT(hoursOT);
    setHoursDT(hoursDT);
    modalRate.current.openModal();
  }

  return (
    <React.Fragment>
      {data.deleted && (
        <Alert variant='danger' className='mt-3'>
          This report was deleted.
        </Alert>
      )}
      {isNotFinished && (
        <Alert variant='warning' className='mt-3'>
          This report may be incomplete, it contains unfinished work orders.
        </Alert>
      )}
      <Card>
        <Card.Header>Mixer Report &#35;{data.id}</Card.Header>
        <Card.Body>
          <InfoTable
            elements={[
              { title: 'Mixer', content: data.mixer.name, link: '/users/' + data.mixer.id },
              { title: 'Start Date', content: formatDate(data.startDate) },
              { title: 'End Date', content: formatDate(data.endDate) },
              { title: 'Total Pay', content: formatMoney(data.totalPay) }
            ]}
          />
        </Card.Body>
      </Card>
      <Card className='mt-3'>
        <Card.Body>
          <Table striped size='sm'>
            <thead>
              <tr>
                <th></th>
                <th>Date</th>
                <th>Company</th>
                <th>Project</th>
                <th>Service</th>
                <th>Hours/Markup</th>
                <th>Rate/Cost</th>
                <th>Subtotal</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {data?.workOrders?.length === 0 && data?.pta?.length === 0 && data?.holidays?.length === 0 && (
                <tr>
                  <td colSpan={9}>None</td>
                </tr>
              )}
              {data.workOrders.map((workOrder) => {
                let count = 0;
                const link = <ListLink to={'/workorders/' + workOrder.id}>WO{workOrder.id}</ListLink>;
                return (
                  <React.Fragment key={workOrder.id}>
                    {workOrder.pay.map((p) => {
                      count++;
                      const durationUnit = p.regularDuration == 1 ? 'hr' : 'hrs';
                      const overTimeDurationUnit = p.overTimeDuration == 1 ? 'hr' : 'hrs';
                      const doubleTimeDurationUnit = p.doubleTimeDuration == 1 ? 'hr' : 'hrs';
                      const overrideHoursOTUnit = p.overrideHoursOT == 1 ? 'hr' : 'hrs';
                      const overrideHoursDTUnit = p.overrideHoursDT == 1 ? 'hr' : 'hrs';
                      const company = workOrder?.session?.company || workOrder?.company;
                      const project = workOrder?.session?.project || workOrder?.project;

                      return (
                        <tr key={p.id}>
                          {count === 1 ? link : <td></td>}
                          <td>{count === 1 && formatDateShort(workOrder?.session?.startTime || workOrder?.date)}</td>
                          <td>
                            {count === 1 && (
                              <ListLink to={'/companies/' + company.id} nowrapper>
                                {company.name}
                              </ListLink>
                            )}
                          </td>
                          <td>
                            {count === 1 && (
                              <ListLink to={'/projects/' + project.id} nowrapper>
                                {project.name}
                              </ListLink>
                            )}
                          </td>
                          <td>{p.type.name}</td>
                          <td>
                            {formatTime(p.startTime)}&nbsp;-&nbsp;
                            {formatTime(p.endTime)}
                            <small>
                              &nbsp;({p.regularDuration} {durationUnit})
                            </small>
                            {(p.overTimeDuration > 0 || p.overrideHoursOT > 0) && (
                              <React.Fragment>
                                <br />
                                <small className='ms-4'>
                                  Overtime:&nbsp;
                                  <span className={p.overrideHoursOT >= 0 ? 'strikethrough' : ''}>
                                    {p.overTimeDuration + ' ' + overTimeDurationUnit}
                                  </span>
                                  {p.overrideHoursOT >= 0 ? ' ' + p.overrideHoursOT + ' ' + overrideHoursOTUnit : ''}
                                </small>
                              </React.Fragment>
                            )}
                            {(p.doubleTimeDuration > 0 || p.overrideHoursDT > 0) && (
                              <React.Fragment>
                                <br />
                                <small className='ms-4'>
                                  Doubletime:&nbsp;
                                  <span className={p.overrideHoursDT >= 0 ? 'strikethrough' : ''}>
                                    {p.doubleTimeDuration + ' ' + doubleTimeDurationUnit}
                                  </span>
                                  {p.overrideHoursDT >= 0 ? ' ' + p.overrideHoursDT + ' ' + overrideHoursDTUnit : ''}
                                </small>
                              </React.Fragment>
                            )}
                          </td>
                          <td>
                            <span className={p.overrideRegularPayRate == undefined ? '' : 'strikethrough'}>
                              {formatMoney(p.regularPayRate)}
                            </span>
                            {!data.deleted && (
                              <Button
                                variant='link'
                                onClick={() =>
                                  editRate(p.id, p.overrideRegularPayRate, p.overrideHoursOT, p.overrideHoursDT)
                                }
                              >
                                <Pencil size={10} className={'icon-align-top'} title='Edit Rate' aria-label='edit' />
                              </Button>
                            )}
                            {p.overrideRegularPayRate == undefined ? (
                              ''
                            ) : (
                              <React.Fragment>
                                <br />
                                {formatMoney(p.overrideRegularPayRate)}
                              </React.Fragment>
                            )}
                          </td>
                          <td>
                            {p.overrideServicePay == undefined
                              ? formatMoney(p.servicePay)
                              : formatMoney(p.overrideServicePay)}
                          </td>
                          <td></td>
                        </tr>
                      );
                    })}
                    <tr>
                      <td colSpan={8}></td>
                      <td>
                        {workOrder.overrideWorkOrderPay == undefined
                          ? formatMoney(workOrder.workOrderPay)
                          : formatMoney(workOrder.overrideWorkOrderPay)}
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
              {data?.pta?.map((p, index) => (
                <tr key={p.id}>
                  <td>{index === 0 ? 'PTA' : ''}</td>
                  <td>{formatDateShort(p.date)}</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td>{formatMoney(p.ptaPay)}</td>
                  <td></td>
                </tr>
              ))}
              {data?.pta?.length > 0 && (
                <tr>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td>{formatMoney(data.ptaTotalPay)}</td>
                </tr>
              )}
              {data?.holidays?.map((holiday, index) => (
                <tr key={holiday.id}>
                  <td>{index === 0 ? 'Holiday' : ''}</td>
                  <td>{formatDateShort(holiday.date)}</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td>{formatMoney(holiday.holidayPay)}</td>
                  <td></td>
                </tr>
              ))}
              {data?.holidays?.length > 0 && (
                <tr>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td>{formatMoney(data.holidaysTotalPay)}</td>
                </tr>
              )}
            </tbody>
          </Table>
          <p className='text-end'>
            <b>Total Pay:</b>{' '}
            {data.overrideTotalPay == undefined ? formatMoney(data.totalPay) : formatMoney(data.overrideTotalPay)}
          </p>
        </Card.Body>
      </Card>
      <FormButtons
        type='Report'
        redirectTo={redirectTo}
        showDelete={!data.deleted}
        showPrint={!data.deleted}
        showEmail={!data.deleted}
        emailInfo={data.emails}
      >
        <React.Fragment>Are you sure you want to delete this report?</React.Fragment>
      </FormButtons>

      <ModalForm title='Edit Pay' url={baseURL} ref={modalRate} putId={data.id} draggable>
        <Form.Control type='hidden' name='workOrderServiceId' value={workOrderServiceId} />
        <InputNumber name='rate' value={overridePayRate?.toString()} money perHour />
        <Switch name='customOT' displayName='Edit Overtime' value={customOT} setValue={setCustomOT} />
        {customOT && (
          <React.Fragment>
            <InputTimeDuration name='hoursOT' displayName='Overtime Hours' value={hoursOT} />
            <InputTimeDuration name='hoursDT' displayName='Doubletime Hours' value={hoursDT} />
          </React.Fragment>
        )}
      </ModalForm>
    </React.Fragment>
  );
}
